import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import KapeiLogo from "@/components/KapeiLogo";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!accepted) { setErr("Debes aceptar la política de privacidad."); return; }
    setLoading(true);
    try {
      await register(form);
      toast.success("Cuenta creada");
      navigate("/");
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-6">
        <Link to="/"><KapeiLogo className="mb-8" /></Link>
        <h1 className="mb-2 text-4xl font-black tracking-tight text-slate-900">Crear cuenta</h1>
        <p className="mb-8 text-base text-slate-600">Solo lo necesario para enviar tus pedidos.</p>

        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          {[
            { k: "name", label: "Nombre", type: "text", placeholder: "María López" },
            { k: "email", label: "Email", type: "email", placeholder: "tu@email.com" },
            { k: "phone", label: "Teléfono", type: "tel", placeholder: "600 000 000" },
            { k: "password", label: "Contraseña", type: "password", placeholder: "••••••••" },
          ].map((f) => (
            <div key={f.k}>
              <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">{f.label}</Label>
              {f.type === "password" ? (
                <PasswordInput
                  data-testid={`register-${f.k}`}
                  required={f.k !== "phone"}
                  value={form[f.k]}
                  onChange={update(f.k)}
                  className="mt-2 h-14 rounded-xl border-2 border-slate-300 px-4 text-lg"
                  placeholder={f.placeholder}
                />
              ) : (
                <Input
                  data-testid={`register-${f.k}`}
                  type={f.type}
                  required={f.k !== "phone"}
                  value={form[f.k]}
                  onChange={update(f.k)}
                  className="mt-2 h-14 rounded-xl border-2 border-slate-300 px-4 text-lg"
                  placeholder={f.placeholder}
                />
              )}
            </div>
          ))}

          <label className="flex items-start gap-3 rounded-xl border-2 border-slate-200 p-4">
            <Checkbox
              data-testid="register-privacy"
              checked={accepted}
              onCheckedChange={(v) => setAccepted(!!v)}
              className="mt-1"
            />
            <span className="text-sm text-slate-700">
              Acepto la <Link to="/help#privacidad" className="font-bold text-[#4d7c0f] underline">política de privacidad</Link>.
              Mis archivos se almacenan temporalmente solo para procesar el pedido.
            </span>
          </label>

          {err && <div data-testid="register-error" className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">{err}</div>}

          <Button
            data-testid="register-submit"
            type="submit" disabled={loading}
            className="kapei-btn-huge bg-[#4d7c0f] text-white hover:bg-[#3f6212]"
          >
            {loading ? "Creando…" : "Crear cuenta"}
          </Button>
        </form>

        <p className="mt-8 text-center text-base text-slate-700">
          ¿Ya tienes cuenta?{" "}
          <Link data-testid="goto-login" to="/login" className="font-extrabold text-[#4d7c0f] underline">Entrar</Link>
        </p>
      </div>
    </div>
  );
}
